
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { OptimizedImage } from '@/components/ui/OptimizedImage';
import { Loader2, Settings, LogOut, ShieldAlert } from 'lucide-react';
import { commonToasts } from '@/hooks/toast/toast-translations';

const ProfileInfo = () => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { toast } = useToast();
  const { user: authUser, profile, isLoading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const isLoading = authLoading;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/');
      toast({
        title: commonToasts.logoutSuccess.title,
        description: commonToasts.logoutSuccess.description,
      });
    } catch {
      toast({
        title: "Gabim gjatë daljes",
        description: "Ndodhi një gabim gjatë daljes nga llogaria.",
        variant: "destructive",
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      {/* Pending Store Approval Message */}
      {profile && profile.store_approval_status === 'pending' && (
        <Card className="mb-6 bg-yellow-50 border-yellow-300 dark:bg-yellow-900/30 dark:border-yellow-700">
          <CardHeader>
            <CardTitle className="text-yellow-700 dark:text-yellow-400 flex items-center">
              <ShieldAlert size={20} className="mr-2" />
              Dyqani juaj është në pritje të miratimit
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-yellow-600 dark:text-yellow-300">
              Aplikimi juaj për të hapur një dyqan është marrë dhe po shqyrtohet nga ekipi ynë.
              Zakonisht, ky proces zgjat pak kohë. Deri atëherë, ju mund të përdorni platformën si një përdorues normal.
              Do të njoftoheni kur statusi i dyqanit tuaj të përditësohet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Approved Store Information Card */}
      {profile && profile.store_approval_status === 'approved' && (
        <Card className="mb-6 shadow-lg">
          <CardHeader className="p-0 relative h-48 md:h-56 rounded-t-lg overflow-hidden">
            <OptimizedImage
              src={profile.store_banner_url || '/placeholder.svg'}
              alt={profile.store_name || 'Store Banner'}
              className="w-full h-full object-cover"
              fallbackSrc="/placeholder.svg"
            />
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
              <Avatar className="h-24 w-24 md:h-28 md:w-28 border-4 border-background dark:border-card shadow-xl">
                <AvatarImage 
                  src={profile.profile_image || `https://avatar.vercel.sh/${profile.id}?size=120`} 
                  alt={profile.store_name || 'Store Logo'} 
                />
                <AvatarFallback className="text-3xl">
                  {(profile.store_name || 'S').charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
          </CardHeader>
          <CardContent className="pt-16 text-center"> {/* Adjusted padding-top */}
            <h2 className="text-2xl font-bold mt-2">{profile.store_name || 'Emri i Dyqanit'}</h2>
            {profile.store_description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {profile.store_description}
              </p>
            )}
            <Link to="/profile/settings" className="mt-4 inline-block">
              <Button variant="default" size="lg">
                <Settings size={18} className="mr-2" />
                Ndrysho Cilësimet e Dyqanit
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* General Profile Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Informacioni i Profilit</CardTitle>
          <CardDescription>
            Informacioni juaj bazë i profilit dhe llogarisë.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <p className="text-gray-500">Duke ngarkuar informacionin e profilit...</p>
            </div>
          ) : authUser ? ( 
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="font-medium text-gray-500">Email</p>
                  <p>{authUser.email}</p>
                </div>
                
                <div className="space-y-2">
                  <p className="font-medium text-gray-500">ID e Përdoruesit</p>
                  <p className="text-sm break-all">{authUser.id}</p>
                </div>
                
                {profile && (
                  <>
                    <div className="space-y-2">
                      <p className="font-medium text-gray-500">Emri i Përdoruesit</p>
                      <p>{profile.username || 'Nuk është vendosur'}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="font-medium text-gray-500">Emri i Plotë</p>
                      <p>{profile.full_name || 'Nuk është vendosur'}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="font-medium text-gray-500">Numri i Telefonit</p>
                      <p>{profile.phone_number || 'Nuk është vendosur'}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="font-medium text-gray-500">Vendndodhja</p>
                      <p>{profile.location || 'Nuk është vendosur'}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="font-medium text-gray-500">Roli</p>
                      <p className="capitalize">
                        {profile.role === 'admin' ? 'Administrator' : 
                          (profile.store_approval_status === 'approved' ? 'Dyqan' : 
                          (profile.store_approval_status === 'pending' ? 'Përdorues (Dyqan në pritje)' : 'Përdorues'))
                        }
                      </p>
                    </div>
                  </>
                )}
              </div>
              
              <div className="pt-4 border-t flex items-center justify-between flex-wrap gap-2">
                <Link to="/profile/settings">
                  <Button variant="outline" className="flex items-center gap-2">
                    <Settings size={16} />
                    {(profile?.store_approval_status === 'approved') ? "Cilësimet Personale & Llogaria" : "Ndrysho Cilësimet e Profilit"}
                  </Button>
                </Link>
                <Button 
                  variant="destructive" 
                  className="flex items-center gap-2"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut size={16} />}
                  Dilni
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Nuk u gjet informacion profili. Ju lutemi rifreskoni faqen ose identifikohuni përsëri.</p>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default ProfileInfo;

